import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api, ENDPOINTS } from '../services';
import { TOAST_MESSAGES } from '../shared/appConstants';

// Async thunks
export const fetchUsers = createAsyncThunk('users/fetchUsers', async ({ page = 1, limit = 10, name = '', status = '', role = [] }) => {
  const params = new URLSearchParams({ page, limit });
  if (name.trim()) params.append('name', name.trim());
  if (status) params.append('status', status);
  const roleValues = Array.isArray(role) ? role : (role ? [role] : []);
  if (roleValues.length) params.append('role', roleValues.join(','));
  const response = await api.get(`${ENDPOINTS.USERS}?${params}`);
  if (!response?.data?.data) {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const fetchAllUsers = createAsyncThunk('users/fetchAllUsers', async (params = null) => {
  const isObjectArg = params && typeof params === 'object' && !Array.isArray(params);
  const spaceId = isObjectArg ? params.spaceId ?? params.space_id ?? null : params;
  const statusValues = isObjectArg ? (Array.isArray(params.status) ? params.status : []) : [];

  const queryParams = new URLSearchParams();
  if (spaceId) queryParams.append('space_id', spaceId);
  statusValues.forEach((status) => {
    if (status) queryParams.append('status', status);
  });

  const url = queryParams.toString() ? `${ENDPOINTS.USERS_ALL}?${queryParams.toString()}` : ENDPOINTS.USERS_ALL;
  const response = await api.get(url);
  if (!response?.data?.data || !Array.isArray(response.data.data)) {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const fetchRoles = createAsyncThunk('users/fetchRoles', async () => {
  const response = await api.get(ENDPOINTS.ROLES);
  if (!response?.data?.data || !Array.isArray(response.data.data)) {
    throw new Error('Invalid response format');
  }
  return response.data.data;
});

export const createUser = createAsyncThunk('users/createUser', async (userData, { rejectWithValue }) => {
  try {
    const response = await api.post(ENDPOINTS.USERS, userData);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || TOAST_MESSAGES.USER_CREATE_FAILED;
    return rejectWithValue(errorMessage);
  }
});

export const updateUser = createAsyncThunk('users/updateUser', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await api.put(ENDPOINTS.USER_BY_ID(id), data);
    return response.data;
  } catch (error) {
    const errorMessage = error.response?.data?.error || error.message || TOAST_MESSAGES.USER_UPDATE_FAILED;
    return rejectWithValue(errorMessage);
  }
});

export const deleteUser = createAsyncThunk('users/deleteUser', async (id) => {
  await api.delete(ENDPOINTS.USER_BY_ID(id));
  return id;
});

const userSlice = createSlice({
  name: 'users',
  initialState: {
    users: [],
    allUsers: [],
    roles: [],
    totalUsers: 0,
    currentPage: 1,
    loading: false,
    allUsersLoading: false,
    error: null
  },
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload && typeof action.payload === 'object') {
          const fetchedUsers = Array.isArray(action.payload.users) ? action.payload.users : [];
          const isFirstPage = !action.meta?.arg || !action.meta.arg.page || action.meta.arg.page === 1;
          if (isFirstPage) {
            state.users = fetchedUsers;
          } else {
            const existingIds = new Set(state.users.map(u => u.id));
            const uniqueNewUsers = fetchedUsers.filter(u => !existingIds.has(u.id));
            state.users = [...state.users, ...uniqueNewUsers];
          }
          state.totalUsers = typeof action.payload.total === 'number' ? action.payload.total : 0;
        }
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        if (action.meta?.aborted) return;
        state.error = action.error.message;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.allUsersLoading = true;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.allUsersLoading = false;
        if (Array.isArray(action.payload)) {
          state.allUsers = action.payload;
        }
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.allUsersLoading = false;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.roles = action.payload;
        }
      })
      .addCase(createUser.fulfilled, (state) => {
        // Trigger refetch
      })
      .addCase(updateUser.fulfilled, (state) => {
        // Trigger refetch
      })
      .addCase(deleteUser.fulfilled, (state) => {
        // Trigger refetch
      });
  }
});

export const { setCurrentPage } = userSlice.actions;
export default userSlice.reducer;
